import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const PlanUploadSection = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    projectLocation: '',
    projectTimeline: '',
    additionalNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('projectType', formData.projectType);
      formBody.append('projectLocation', formData.projectLocation);
      formBody.append('projectTimeline', formData.projectTimeline);
      formBody.append('additionalNotes', formData.additionalNotes);

      const response = await fetch('https://readdy.ai/api/form/d6f4j0bv53dtlm7j9h0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          projectLocation: '',
          projectTimeline: '',
          additionalNotes: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
            Upload Your <span className="font-semibold">Project Plans</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Send us your architectural plans or material list and we'll provide a comprehensive quote with exact savings
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left - Process Steps */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="flex items-start">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                1
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Submit Your Plans</h3>
                <p className="text-sm text-gray-600">
                  Upload architectural drawings, material lists, or project specifications
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                2
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-base text-gray-900 mb-1">We Analyze & Source</h3>
                <p className="text-sm text-gray-600">
                  Our team reviews your plans and sources materials directly from manufacturers
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                3
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-base text-gray-900 mb-1">Receive Detailed Quote</h3>
                <p className="text-sm text-gray-600">
                  Get itemized pricing with clear savings breakdown within 48-72 hours
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl p-6 border border-secondary-200">
              <i className="ri-file-list-3-line text-3xl text-secondary-600 mb-3"></i>
              <h3 className="font-semibold text-base text-gray-900 mb-2">Accepted File Types</h3>
              <p className="text-sm text-gray-600">
                PDF, DWG, Excel, Word, Images (JPG, PNG)
              </p>
            </div>
          </motion.div>

          {/* Right - Upload Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <form id="plan-upload-form" onSubmit={handleSubmit} data-readdy-form className="bg-gray-50 rounded-2xl p-8 border border-gray-200 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-gray-900 mb-2">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select project type</option>
                    <option value="Custom Home Build">Custom Home Build</option>
                    <option value="Real Estate Development">Real Estate Development</option>
                    <option value="Renovation/Remodel">Renovation/Remodel</option>
                    <option value="Commercial Construction">Commercial Construction</option>
                    <option value="Industrial Project">Industrial Project</option>
                    <option value="Multi-Family Residential">Multi-Family Residential</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="projectLocation" className="block text-sm font-medium text-gray-900 mb-2">
                    Project Location *
                  </label>
                  <input
                    type="text"
                    id="projectLocation"
                    name="projectLocation"
                    value={formData.projectLocation}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="City, Province/State"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectTimeline" className="block text-sm font-medium text-gray-900 mb-2">
                  Project Timeline *
                </label>
                <select
                  id="projectTimeline"
                  name="projectTimeline"
                  value={formData.projectTimeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="Immediate (0-3 months)">Immediate (0-3 months)</option>
                  <option value="Short-term (3-6 months)">Short-term (3-6 months)</option>
                  <option value="Mid-term (6-12 months)">Mid-term (6-12 months)</option>
                  <option value="Long-term (12+ months)">Long-term (12+ months)</option>
                  <option value="Planning Phase">Planning Phase</option>
                </select>
              </div>

              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-900 mb-2">
                  Additional Notes
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder="Any specific requirements, questions, or details about your project..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.additionalNotes.length}/500 characters
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-3"></i>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Note:</strong> After submitting this form, we'll send you a secure link to upload your files
                  </p>
                  <p className="text-xs text-gray-500">
                    Accepted formats: PDF, DWG, Excel, Word, JPG, PNG (Max 50MB per file)
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-base font-semibold rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Get Upload Link'}
                {!isSubmitting && <i className="ri-arrow-right-line ml-2"></i>}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <i className="ri-check-line mr-2"></i>
                  Success! Check your email for the secure file upload link.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or email us directly.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PlanUploadSection;
